<template>
  <v-container fluid grid-list-md>
    <v-card class="pa-2 mb-4">
      <v-layout row justify-center align-center wrap>
        <v-flex xs4>
          <v-select
            v-model="selectedRoleName"
            :items="roles"
            :label="$vuetify.t('$vuetify.RoleEdit.userRole')"
            item-text="name"
            hide-details
            class="pa-1"
            @change="loadSelectedRole()"/>
        </v-flex>
        <v-flex xs8>
          <v-layout row justify-center align-content-center wrap>
            <v-flex xs4>
              <v-tooltip :disabled="canCreate" bottom>
                <v-btn block class="ma-0" slot="activator" :disabled="!canCreate" color="primary"
                       @click="showRoleCreateDialog">
                  <v-icon small>add</v-icon>
                  {{ $vuetify.t('$vuetify.RoleEdit.createRole') }}
                </v-btn>
                <span>{{ $vuetify.t('$vuetify.Permissions.noPermission') }}</span>
              </v-tooltip>
            </v-flex>

            <v-flex xs4>
              <v-tooltip :disabled="canDelete" bottom>
                <v-btn block class="ma-0" slot="activator" :disabled="!canDelete || !selectedRole" color="error"
                       @click="deleteRole">
                  <v-icon small>delete</v-icon>
                  {{ $vuetify.t('$vuetify.RoleEdit.deleteRole') }}
                </v-btn>
                <span>{{ $vuetify.t('$vuetify.Permissions.noPermission') }}</span>
              </v-tooltip>
            </v-flex>

            <v-flex xs4>
              <v-tooltip :disabled="canEdit" bottom>
                <v-btn block class="ma-0" slot="activator"
                       :disabled="!selectedRoleChanged || !canEdit || !selectedRole" color="secondary"
                       v-on:click="saveRole">
                  <v-icon small>save</v-icon>
                  {{ $vuetify.t('$vuetify.RoleEdit.saveChanges') }}
                </v-btn>
                <span>{{ $vuetify.t('$vuetify.Permissions.noPermission') }}</span>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card v-if="selectedRole" class="pa-2">
      <v-list two-line subheader
              v-for="(permission, key, index) in permissions"
              :key="permission.title">

        <v-subheader style="font-size: 15px" @click="selectedPermissionTab = permission.name">
          {{ $vuetify.t('$vuetify.Permissions.' + permission.name + '.title') }}
        </v-subheader>

        <v-divider v-if="index !== Object.keys(permissions).length - 1"></v-divider>

        <v-expand-transition>
          <div v-show="selectedPermissionTab === permission.name">

            <v-list-tile
              v-for="permissionItem in permission.items" :key="permissionItem"
              @click="onSelectPermission($event, permissionItem, permission.validityRequired)">

              <v-list-tile-action>
                <v-checkbox
                  :disabled="!canEdit"
                  :input-value="hasPermission(permissionItem)"
                  @click.capture.stop="onSelectPermission($event, permissionItem, permission.validityRequired)"/>
              </v-list-tile-action>

              <v-list-tile-content>
                <v-container fluid>
                  <v-layout row justify-center align-content-center xs12>
                    <v-flex xs8>
                      <v-list-tile-title>
                        {{
                          $vuetify.t('$vuetify.Permissions.' + permission.name + '.' + permissionItem + '.title')
                        }}
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{
                          $vuetify.t('$vuetify.Permissions.' + permission.name + '.' + permissionItem + '.description')
                        }}
                      </v-list-tile-sub-title>
                    </v-flex>
                    <v-flex xs4>
                      <v-select
                        v-if="hasPermission(permissionItem) && permission.validityRequired"
                        :disabled="!canEdit"
                        :label="$vuetify.t('$vuetify.RoleEdit.validFor')"
                        v-model="selectedRole.permissions[permissionItem][0]"
                        :items="permissionValidity"
                        item-value="value"
                        :item-text="item => $vuetify.t('$vuetify.RoleEdit.validityTypes.' + item.translationKey)"
                        :error="selectedRole.permissions[permissionItem].length === 0"
                        @click.native.stop/>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-list-tile-content>
            </v-list-tile>
          </div>
        </v-expand-transition>
      </v-list>
    </v-card>

    <role-add-dialog
      ref="roleAddDialogRef">
    </role-add-dialog>

    <snack-bar
      ref="snackBarRef">
    </snack-bar>

  </v-container>
</template>

<script>

import api from '@/api'
import RoleAddDialog from './dialogs/RoleAddDialog'
import SnackBar from './util/SnackBar'
import _ from 'lodash'

export default {
  name: 'RoleEdit',
  components: {
    RoleAddDialog,
    SnackBar
  },
  data: function () {
    return {
      roles: [],
      selectedRoleName: null,
      selectedRole: null,
      copyOfSelectedRole: {},
      selectedRoleChanged: false,
      hasPermissionsChanged: false,
      selectedPermissionTab: 'idea',
      permissions: {
        idea: {
          name: 'idea',
          items: ['create_idea', 'delete_idea', 'support_idea', 'edit_idea', 'approve_idea', 'vote_idea', 'mark_idea_winner'],
          validityRequired: true
        },
        comment: {
          name: 'comment',
          items: ['create_comment', 'delete_comment', 'support_comment', 'edit_comment'],
          validityRequired: true
        },
        topic: {
          name: 'topic',
          items: ['create_topic', 'edit_topic', 'delete_topic', 'add_topic_idea', 'change_topic_phase'],
          validityRequired: true
        },
        user: {
          name: 'user',
          items: ['create_user', 'delete_user', 'edit_user'],
          validityRequired: false
        },
        role: {
          name: 'role',
          items: ['create_role', 'delete_role', 'edit_role'],
          validityRequired: false
        },
        idea_space: {
          name: 'idea_space',
          items: ['create_room', 'delete_room', 'edit_room'],
          validityRequired: false
        },
        category: {
          name: 'category',
          items: ['delete_category', 'create_category', 'edit_category'],
          validityRequired: false
        },
        admin: {
          name: 'admin',
          items: ['see_admin'],
          validityRequired: false
        },
        pages: {
          name: 'pages',
          items: ['create_page', 'delete_page', 'edit_page'],
          validityRequired: false
        }
      },
      permissionValidity: [
        {value: 'school', translationKey: 'school'},
        {value: 'idea_space', translationKey: 'idea_space'},
        {value: 'school_and_idea_space', translationKey: 'school_and_idea_space'},
        {value: 'all', translationKey: 'all'}]
    }
  },
  watch: {
    selectedRole: {
      handler (oldVal, newVal) {
        if (this.selectedRole != null && _.isEqual(oldVal, newVal)) {
          this.selectedRoleChanged = true
        }
      },
      deep: true
    }
  },
  beforeMount: function () {
    this.fetchRoles()
  },
  computed: {
    canCreate: function () {
      return this.$hasPermission(this.$permissions.role.items.createRole)
    },
    canDelete: function () {
      return this.$hasPermission(this.$permissions.role.items.deleteRole)
    },
    canEdit: function () {
      return this.$hasPermission(this.$permissions.role.items.editRole)
    }
  },
  methods: {
    hasPermission: function (permissionTitle) {
      return permissionTitle in this.selectedRole.permissions
    },

    loadSelectedRole: function () {
      this.fetchRoles().then(res => {
        this.selectedRole = _.cloneDeep(this.findRoleByName(this.selectedRoleName))
        this.selectedRoleChanged = false
      })
    },

    onSelectPermission: function (event, selectedPermission, validityRequired) {
      // Preventing default checkbox behaviour
      event.preventDefault()

      if (!this.selectedRole.permissions[selectedPermission]) {
        let validity = validityRequired ? ['idea_space'] : ['school']
        this.$set(this.selectedRole.permissions, selectedPermission, validity)
      } else {
        this.$delete(this.selectedRole.permissions, selectedPermission)
      }
    },

    showRoleCreateDialog: function () {
      this.$refs.roleAddDialogRef.showDialog()
    },

    fetchRoles: async function () {
      await api.roles.getRoles(this.$store.getters.selected_school).then(res => {
        if (res.status < 400) {
          this.roles = res.data
        } else {
          this.$refs.snackBarRef.showSnackbar('$vuetify.RoleEdit.fetchError')
        }
      })
    },

    saveRole: function () {
      if (this.selectedRole) {
        let rolePatch = {permissions: this.selectedRole.permissions}
        api.roles.updateRole(this.selectedRole.id, rolePatch).then(res => {
          if (res.status < 400) {
            this.selectedRoleChanged = false
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.saveSuccess'))
          } else {
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.saveError'))
          }
        })
      }
    },

    createRole: function (roleName) {
      if (!roleName || roleName === '' || this.findRoleByName(roleName)) {
        return
      }
      api.roles.createRole(roleName, this.$store.getters.selected_school)
        .then(res => {
          if (res.status < 400) {
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.createSuccess'))
            this.fetchRoles().then(res => {
              this.selectedRoleName = roleName
            })
          } else {
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.createError'))
          }
        })
    },

    deleteRole: function () {
      if (this.selectedRole) {
        api.roles.deleteRole(this.selectedRole.id).then(res => {
          if (res.status < 400) {
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.deleteSuccess'))
            this.selectedRole = null
            this.fetchRoles()
          } else {
            this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.deleteError'))
          }
        }).catch(() => {
          this.$refs.snackBarRef.showSnackbar(this.$vuetify.t('$vuetify.RoleEdit.deleteError'))
        })
      }
    },

    findRoleByName: function (roleName) {
      return this.roles.find(role => {
        return role.name === roleName
      })
    }
  }
}
</script>
<style lang="scss">
.permissionTitle {
  font-size: 1.25em;
  font-weight: bold;
}

i {
  padding-right: 8px;
}
</style>
